/**
 * BookOnBehalfModal (SPEC G1 — booking-event-integration, R2).
 *
 * A thin shadcn/ui Dialog that embeds the tenant's Cal.com scheduling page in a
 * SANDBOXED iframe, pre-filled with the contact's name + email via Cal.com's
 * ?name=&email= query params. The operator books directly inside the embed;
 * Cal.com fires the BOOKING_CREATED webhook to the dialer (R1), which resolves the
 * Contact + upserts the Opportunity automatically. There is NO Cal.com API call
 * from the frontend — this is an iframe-only embed (no API-key management).
 *
 * After the operator books, they close the modal; ``onClose`` triggers the
 * parent's refresh so the updated Opportunity surfaces. The modal does not detect
 * booking completion — the webhook is the source of truth (a confirming note is
 * shown beneath the embed).
 *
 * If the tenant has no ``cal_embed_url`` configured, the body renders a guidance
 * message instead of an iframe.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface BookOnBehalfModalProps {
  open: boolean;
  onClose: () => void;
  contactName?: string | null;
  contactEmail?: string | null;
  /** The tenant's Cal.com scheduling link (from GET /api/tenant/config). */
  calEmbedUrl?: string | null;
}

/** Build the embed src with the contact's name + email pre-filled. */
export function buildEmbedSrc(
  calEmbedUrl: string,
  contactName?: string | null,
  contactEmail?: string | null
): string {
  const sep = calEmbedUrl.includes('?') ? '&' : '?';
  const params = new URLSearchParams();
  if (contactName) params.set('name', contactName);
  if (contactEmail) params.set('email', contactEmail);
  const qs = params.toString();
  return qs ? `${calEmbedUrl}${sep}${qs}` : calEmbedUrl;
}

export function BookOnBehalfModal({
  open,
  onClose,
  contactName,
  contactEmail,
  calEmbedUrl,
}: BookOnBehalfModalProps) {
  const title = `Book on behalf of ${contactName || contactEmail || 'contact'}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Schedule directly below. When you book, Cal.com confirms the
            appointment and the dialer updates this contact's deal automatically.
          </DialogDescription>
        </DialogHeader>

        {calEmbedUrl ? (
          <div className="space-y-2">
            <iframe
              title="Cal.com scheduling"
              data-testid="book-on-behalf-iframe"
              src={buildEmbedSrc(calEmbedUrl, contactName, contactEmail)}
              width="100%"
              height="600px"
              frameBorder="0"
              // Sandbox the embed: allow it to run scripts + same-origin (Cal.com
              // needs both to render its scheduler) and submit the booking form,
              // but contain it from navigating the top frame.
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
            <p className="text-xs text-muted-foreground">
              The booking is confirmed by Cal.com's webhook — close this window
              after you book and the deal will refresh.
            </p>
          </div>
        ) : (
          <div
            className="py-8 text-center text-sm text-muted-foreground"
            data-testid="book-on-behalf-not-configured"
          >
            Cal.com link not configured for this tenant. Add cal_embed_url to
            tenant settings.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
